import { useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { type ModelMetadata } from "resources/models"
import { OK, isLoading, type DataStatus } from "helpers/data-statuses"
import { type SelectedInstrument } from "helpers/types/instruments/selected"
import { getEligibleModels, getMostUsedOrDefaultModelId } from "helpers/models"
import { useAccountAlias } from "resources/account"
import { useDashboardModelsWithCoverage } from "./use-dashboard-models-with-coverage"
import { useCoreActions } from "./use-core-actions"

/**
 * A selected model resolving to null metadata on an OK status is inaccessible for this account
 * (pi-server returns 200 + `data.model: null`; see graphql/model/resolvers.js). Reselect an
 * accessible model via the selector's coverage-aware, most-used pick, excluding known-inaccessible
 * ids, and warn. Gated on `isPreferencesLoaded` so coverage isn't computed against an unsettled
 * instrument; the coverage queries fire only once an inaccessible selection is detected.
 */
export function useModelAccessFallback(
  modelId: string | null,
  modelMetadata: ModelMetadata | null,
  modelMetadataStatus: DataStatus,
  instrument: SelectedInstrument | null,
  isPreferencesLoaded: boolean,
): void {
  const dispatch = useDispatch()
  const { setModelId } = useCoreActions()
  const account = useAccountAlias()
  const inaccessibleModelIds = useRef<Set<string>>(new Set())
  const handledModelId = useRef<string | null>(null)

  // Per-account: the dashboard stays mounted across a superuser account switch, so clear the
  // exclusions/latch on account change — else one account's rejects leak into the next.
  useEffect(() => {
    inaccessibleModelIds.current = new Set()
    handledModelId.current = null
  }, [account])

  const shouldEvaluate = !!modelId && isPreferencesLoaded && modelMetadataStatus === OK && !modelMetadata
  // The selector treats coverage as a hard gate (disables uncovered options, declines to auto-pick
  // one), but here it's only a preference — hence the local alias.
  const { modelsWithCoverage, status, requiresResourceCoverage: preferResourceCoverage, portfolios, portfoliosStatus } =
    useDashboardModelsWithCoverage(shouldEvaluate ? instrument : null, modelId)

  useEffect(() => {
    if (!shouldEvaluate || !modelId || status !== OK || !modelsWithCoverage?.length) {
      // Off an inaccessible selection: clear the latch so re-selecting the same model later re-fires.
      if (!shouldEvaluate) {
        handledModelId.current = null
      }
      return
    }
    // Portfolios only rank the pick — wait while they load, but don't block the escape on their error.
    if (isLoading(portfoliosStatus)) {
      return
    }
    // Act once per contiguous inaccessible selection — deps can churn (refetch) while modelId is
    // unchanged, and the no-alternative path never changes it. Id-scoped (reset on leave) so
    // re-selecting the same model still recovers.
    if (handledModelId.current === modelId) {
      return
    }
    handledModelId.current = modelId
    // Reaching here means modelId is inaccessible (shouldEvaluate) — exclude it from the pick below.
    inaccessibleModelIds.current.add(modelId)
    const previousName = modelsWithCoverage.find(model => model.id === modelId)?.name ?? modelId
    // Coverage is a preference here, not a hard gate (this runs with no picker to recover): prefer
    // covered candidates, but escape to any accessible model so the dashboard is never stranded.
    const accessibleModels = getEligibleModels(modelsWithCoverage, false, inaccessibleModelIds.current)
    const coveredModels = getEligibleModels(modelsWithCoverage, preferResourceCoverage, inaccessibleModelIds.current)
    const candidateModels = coveredModels.length ? coveredModels : accessibleModels
    const nextModelId = getMostUsedOrDefaultModelId(portfolios ?? [], candidateModels)
    if (!nextModelId || nextModelId === modelId) {
      dispatch(NotificationCreators.add(
        "warning",
        null,
        `The account doesn't have access to ${previousName}, and no other model is available.`,
      ))
      return
    }
    const nextName = modelsWithCoverage.find(model => model.id === nextModelId)?.name ?? nextModelId
    setModelId(nextModelId)
    dispatch(NotificationCreators.add(
      "warning",
      null,
      `The account doesn't have access to ${previousName}. Switched to ${nextName}.`,
    ))
  }, [shouldEvaluate, modelId, status, portfoliosStatus, modelsWithCoverage, preferResourceCoverage, portfolios, setModelId, dispatch])
}
